import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  CreatePickTaskRequest,
  PickConfig,
  PickedConsumable,
  PickedSerial,
  PickTask,
  PickTaskListFilters,
  PrepareRequest,
  PreparedTask,
} from '../@types/picktask.type';
import {
  addPickIssue,
  cancelPickTask,
  createPickTask,
  getPickConfig,
  getPickTaskById,
  getPickTasks,
  preparePickTask,
  savePickTaskScans,
  updatePickConfig,
  updatePickTaskSource,
} from '../api/picktask';
import { PickConfigKey, PickTaskKey, PickTasksKey } from '../configs/picktask';

const invalidate = async (queryClient: ReturnType<typeof useQueryClient>, id?: number) => {
  if (id) await queryClient.invalidateQueries({ queryKey: [PickTaskKey, id] });
  await queryClient.invalidateQueries({ queryKey: [PickTasksKey] });
};

export const useGetPickTasks = (
  filters?: PickTaskListFilters,
  enabled = true
): UseQueryResult<PickTask[]> =>
  useQuery({
    queryKey: [PickTasksKey, filters],
    queryFn: ({ signal }) => getPickTasks(filters, signal),
    enabled,
    gcTime: 0,
  });

export const useGetPickTask = (id: number): UseQueryResult<PickTask | undefined> =>
  useQuery({
    queryKey: [PickTaskKey, id],
    queryFn: ({ signal }) => (id ? getPickTaskById(id, signal) : undefined),
    enabled: id > 0,
  });

export const usePreparePickTask = (): UseMutationResult<PreparedTask[], Error, PrepareRequest> =>
  useMutation({ mutationFn: (data: PrepareRequest) => preparePickTask(data) });

export const useCreatePickTask = (): UseMutationResult<PickTask, Error, CreatePickTaskRequest> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePickTaskRequest) => createPickTask(data),
    onSuccess: (task) => invalidate(queryClient, task.id),
  });
};

export const useCancelPickTask = (): UseMutationResult<PickTask, Error, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelPickTask(id),
    onSuccess: (task) => invalidate(queryClient, task.id),
  });
};

export const useUpdatePickTaskSource = (): UseMutationResult<
  PickTask,
  Error,
  { id: number; fromLocationNodeId: number | null }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fromLocationNodeId }) => updatePickTaskSource(id, fromLocationNodeId),
    onSuccess: (task) => invalidate(queryClient, task.id),
  });
};

export const useSavePickTaskScans = (): UseMutationResult<
  PickTask,
  Error,
  { id: number; pickedSerials: PickedSerial[]; pickedConsumables: PickedConsumable[] }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pickedSerials, pickedConsumables }) =>
      savePickTaskScans(id, pickedSerials, pickedConsumables),
    onSuccess: (task) => invalidate(queryClient, task.id),
  });
};

export const useGetPickConfig = (): UseQueryResult<PickConfig> =>
  useQuery({ queryKey: [PickConfigKey], queryFn: ({ signal }) => getPickConfig(signal) });

export const useUpdatePickConfig = (): UseMutationResult<PickConfig, Error, number | null> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaultSourceNodeId: number | null) => updatePickConfig(defaultSourceNodeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PickConfigKey] }),
  });
};

export const useAddPickIssue = (): UseMutationResult<
  unknown,
  Error,
  {
    id: number;
    data: { shipmentId?: number; partTypeId?: number; quantity: number; notes?: string };
  }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => addPickIssue(id, data),
    onSuccess: (_r, { id }) => invalidate(queryClient, id),
  });
};
